/**
 * Mutable PJM board state helpers — docs/INTERNAL_ADU_UTAH_PJM.md
 */
import { WORKSTREAMS } from './aduUtahPjmCanvas';

export const PJM_STORAGE_KEY = 'construx-adu-utah-pjm-v1';

export function makeItemId() {
  return `i_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function toItems(strings = []) {
  return strings.map((text) => ({ id: makeItemId(), text: String(text) }));
}

function normalizeItem(item) {
  if (!item || typeof item !== 'object') {
    return { id: makeItemId(), text: String(item ?? '') };
  }
  return {
    id: item.id || makeItemId(),
    text: String(item.text ?? ''),
  };
}

function normalizeSubcard(block) {
  return {
    id: block?.id || makeItemId(),
    title: String(block?.title || 'Untitled'),
    items: (block?.items || []).map(normalizeItem),
  };
}

function normalizeWorkstream(ws) {
  return {
    id: ws.id,
    label: ws.label,
    title: ws.title,
    objective: ws.objective,
    knowns: (ws.knowns || []).map(normalizeItem),
    unknowns: (ws.unknowns || []).map(normalizeItem),
    considerations: (ws.considerations || []).map(normalizeItem),
    nextSteps: (ws.nextSteps || []).map(normalizeItem),
    subItems: (ws.subItems || []).map(normalizeSubcard),
  };
}

export function hydrateFromSeed(seed = WORKSTREAMS) {
  return {
    workstreams: seed.map((ws) =>
      normalizeWorkstream({
        ...ws,
        knowns: toItems(ws.knowns),
        unknowns: toItems(ws.unknowns),
        considerations: toItems(ws.considerations),
        nextSteps: toItems(ws.nextSteps),
        subItems: (ws.subItems || []).map((block) => ({
          id: makeItemId(),
          title: block.title,
          items: toItems(block.items),
        })),
      })
    ),
    bank: [],
  };
}

export function loadBoard() {
  try {
    const raw = localStorage.getItem(PJM_STORAGE_KEY);
    if (!raw) return hydrateFromSeed();
    const parsed = JSON.parse(raw);
    if (!parsed?.workstreams?.length) return hydrateFromSeed();
    return {
      workstreams: parsed.workstreams.map(normalizeWorkstream),
      bank: (parsed.bank || []).map(normalizeItem),
    };
  } catch {
    return hydrateFromSeed();
  }
}

export function saveBoard(board) {
  try {
    localStorage.setItem(PJM_STORAGE_KEY, JSON.stringify(board));
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {{ kind: 'bank' } | { kind: 'section', wsId: string, section: string } | null} */
export function parseDropTarget(targetKey) {
  if (!targetKey) return null;
  if (targetKey === 'bank') return { kind: 'bank' };
  const sep = targetKey.indexOf('::');
  if (sep < 0) return null;
  const wsId = targetKey.slice(0, sep);
  const section = targetKey.slice(sep + 2);
  if (!wsId || !section) return null;
  return { kind: 'section', wsId, section };
}

function getListRef(board, loc) {
  if (loc.kind === 'bank') {
    return { parent: board, key: 'bank', list: board.bank };
  }
  const ws = board.workstreams.find((w) => w.id === loc.wsId);
  if (!ws) return null;
  if (loc.section === 'knowns') return { parent: ws, key: 'knowns', list: ws.knowns };
  if (loc.section === 'unknowns') return { parent: ws, key: 'unknowns', list: ws.unknowns };
  if (loc.section === 'considerations') {
    return { parent: ws, key: 'considerations', list: ws.considerations };
  }
  if (loc.section === 'next') return { parent: ws, key: 'nextSteps', list: ws.nextSteps };
  if (loc.section.startsWith('sub:')) {
    const key = loc.section.slice(4);
    const byId = ws.subItems.find((b) => b.id === key);
    if (byId) return { parent: byId, key: 'items', list: byId.items };
    const idx = Number(key);
    if (!Number.isNaN(idx) && ws.subItems[idx]) {
      const block = ws.subItems[idx];
      return { parent: block, key: 'items', list: block.items };
    }
  }
  return null;
}

function cloneBoard(board) {
  return structuredClone(board);
}

function findItemLocation(board, itemId) {
  const bankIdx = board.bank.findIndex((i) => i.id === itemId);
  if (bankIdx >= 0) {
    return { loc: { kind: 'bank' }, index: bankIdx, item: board.bank[bankIdx] };
  }
  for (const ws of board.workstreams) {
    const sections = [
      ['knowns', ws.knowns],
      ['unknowns', ws.unknowns],
      ['considerations', ws.considerations],
      ['next', ws.nextSteps],
      ...ws.subItems.map((b) => [`sub:${b.id}`, b.items]),
    ];
    for (const [section, list] of sections) {
      const index = list.findIndex((i) => i.id === itemId);
      if (index >= 0) {
        return {
          loc: { kind: 'section', wsId: ws.id, section },
          index,
          item: list[index],
        };
      }
    }
  }
  return null;
}

export function createItem(board, text) {
  const next = cloneBoard(board);
  const trimmed = text.trim();
  if (!trimmed) return board;
  next.bank.unshift({ id: makeItemId(), text: trimmed });
  return next;
}

export function updateItemText(board, itemId, text) {
  const next = cloneBoard(board);
  const found = findItemLocation(next, itemId);
  if (!found) return board;
  found.item.text = text;
  return next;
}

export function deleteItem(board, itemId) {
  const next = cloneBoard(board);
  const found = findItemLocation(next, itemId);
  if (!found) return board;
  const ref = getListRef(next, found.loc);
  if (!ref) return board;
  ref.parent[ref.key] = ref.list.filter((i) => i.id !== itemId);
  return next;
}

export function moveItem(board, itemId, toLoc, beforeItemId = null) {
  const next = cloneBoard(board);
  const found = findItemLocation(next, itemId);
  if (!found) return board;
  if (beforeItemId === itemId) return board;

  const fromRef = getListRef(next, found.loc);
  if (!fromRef) return board;
  const item = { ...found.item };
  fromRef.parent[fromRef.key] = fromRef.list.filter((i) => i.id !== itemId);

  const toRef = getListRef(next, toLoc);
  if (!toRef) return board;

  const destList = [...toRef.parent[toRef.key]].filter((i) => i.id !== itemId);
  let insertAt = destList.length;
  if (beforeItemId) {
    const bi = destList.findIndex((i) => i.id === beforeItemId);
    if (bi >= 0) insertAt = bi;
  }
  destList.splice(insertAt, 0, item);
  toRef.parent[toRef.key] = destList;
  return next;
}

export function addSubcard(board, wsId, title = 'New subcard') {
  const next = cloneBoard(board);
  const ws = next.workstreams.find((w) => w.id === wsId);
  if (!ws) return board;
  const card = { id: makeItemId(), title, items: [] };
  ws.subItems.push(card);
  return { board: next, subId: card.id };
}

export function renameSubcard(board, wsId, subId, title) {
  const next = cloneBoard(board);
  const ws = next.workstreams.find((w) => w.id === wsId);
  const block = ws?.subItems?.find((b) => b.id === subId);
  if (!block) return board;
  block.title = title;
  return next;
}

export function sectionKeysFor(ws) {
  const keys = ['knowns', 'unknowns'];
  (ws.subItems || []).forEach((b) => keys.push(`sub:${b.id}`));
  keys.push('considerations', 'next');
  return keys;
}
